import { Holding_SummariseForConsumer } from '@rabbit/bizproc/client';
import {
  DTHoldingProxy,
  DTVendable,
  FBD_Consumer_Private,
  FBD_HoldingProxy,
  FBD_Vendable,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  FBD_Holding_Private,
} from '@rabbit/data/types';
import { t } from 'i18next';
import { useCallback } from 'react';

export function useGetConsumerHoldings(consumerId: string) {
  /** Runs the holding summariser (more detailed, more computationally intensive) for a single holding */
  // TODO: @DEV-754: This should all be handled by Holding_Proxy.
  const getSingleHolding = async (holdingId: string) => {
    // if (!consumerId) return new Error('A valid consumer persona is required');

    const summary = await Holding_SummariseForConsumer(holdingId);
    return summary || [];
  };

  const getSingleHoldingProxy = async (holdingId: string) => {
    const holdingProxy = await FBD_HoldingProxy.get(holdingId);
    return holdingProxy;
  };

  const getSingleHoldingProxyByHoldingLink = async (holdingLink: string) => {
    const holdingProxy = await FBD_HoldingProxy.query()
      .where('holdingLink', '==', holdingLink)
      .getContents();
    return holdingProxy[0] || null;
  };

  const getAllHoldingProxies = useCallback(
    async (
      consumerIdKey: string | null = null
    ): Promise<DTHoldingProxy[] | Error> => {
      const targetConsumerId = consumerIdKey ?? consumerId;
      if (!targetConsumerId)
        return new Error('A valid consumer persona is required');

      const tenantLink = t('tenantLink');

      const proxyQuery = FBD_HoldingProxy.query()
        .where(
          'mfr',
          '==',
          PersonaTypeSingleLetter.Manufacturer +
            PersonaIdTypeSplitter +
            tenantLink
        )
        .where('consumer', '==', targetConsumerId);
      const holdingProxies = await proxyQuery.getContents();
      return holdingProxies;
    },
    [consumerId]
  );

  const getCurrentHoldingProxy = async (holdingId: string) => {
    if (!consumerId) return new Error('A valid consumer persona is required');

    const tenantLink = t('tenantLink');

    const proxyQuery = FBD_HoldingProxy.query()
      .where('consumer', '==', consumerId)
      .where(
        'mfr',
        '==',
        PersonaTypeSingleLetter.Manufacturer +
          PersonaIdTypeSplitter +
          tenantLink
      )
      .where('holdingLink', '==', holdingId);
    const holdingProxies = await proxyQuery.getContents();
    return holdingProxies[0] || null;
  };

  const getVendableByHoldingLink = useCallback(
    async (holdingId: string): Promise<DTVendable | null | Error> => {
      if (!holdingId) return new Error('A valid holding link is required');
      const holdingProxy = await FBD_Holding_Private.get(holdingId);
      return holdingProxy?.vendable
        ? await FBD_Vendable.get(holdingProxy.vendable)
        : new Error('Vendable not found');
    },
    []
  );

  return {
    getSingleHolding,
    getSingleHoldingProxy,
    getSingleHoldingProxyByHoldingLink,
    getAllHoldingProxies,
    getCurrentHoldingProxy,
    getVendableByHoldingLink,
  };
}
